import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from 'react';
import { Search, RotateCcw, Download, RefreshCw, ChevronDown } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { DateRangePicker } from 'rsuite';
import 'rsuite/dist/rsuite.min.css';
import * as XLSX from 'xlsx';
import Select from 'react-select';
import { useCurrentDomain } from '../../context/DomainContext';
import { getAIVerificationSummaryReport } from '../../../src/services/masterDataService';
import moment from 'moment';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AIVerificationSummaryReport.css';
import { predefinedBottomRanges, selectStyles, useColumnResizePct } from '../../helpers';

const BRAND_GREEN = '#0aa375';
const TREND_WEEKS_BACK = 5;
const CHART_MOBILE_BREAKPOINT = 950;

const TABS = [
    { key: 'Summary',       label: 'Summary' },
    { key: 'UserBreakdown', label: 'Company and User Wise Detail' },
    { key: 'Details',       label: 'Bill Wise Detatil' },
];

const DETAILS_COLUMNS = [
    { label: 'Verif. Src',          key: 'verificationSource' },   // 0
    { label: 'Comp Name',           key: 'companyName' },          // 1
    { label: 'Bill No',             key: 'billNumber' },           // 2
    { label: 'Verif. Type',         key: 'verificationTypeName' }, // 3
    { label: 'Description',         key: 'description' },          // 4
    { label: 'Verif. Date',         key: 'verificationDate' },     // 5
    { label: 'Created By',          key: 'createdUserName' },      // 6
    { label: 'Created Date',        key: 'createdDate' },          // 7
];

const USER_BREAKDOWN_COLUMNS = [
    { label: 'Verif. Src',          key: 'verificationSource' },   // 0
    { label: 'Comp Name',           key: 'companyName' },          // 1
    { label: 'User Name',           key: 'userName' },             // 2
    { label: 'Email',               key: 'email' },                // 3
    { label: 'Verif. Count',        key: 'verificationCount' },    // 4
];

const SOURCE_OPTIONS = [
    { value: 'all',           label: 'All' },
    { value: 'aiModule',      label: 'AI Module' },
    { value: 'manual',        label: 'Manual' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const pageSizeSelectOptions = PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }));


const getSourceBadgeStyles = (src) => {
    switch ((src || '').toUpperCase()) {
        case 'AI MODULE': return { backgroundColor: '#e6f7f2', color: BRAND_GREEN };
        case 'MANUAL':    return { backgroundColor: '#f1f5f9', color: '#475569' };
        default:          return { backgroundColor: '#f1f5f9', color: '#475569' };
    }
};

const fmtDate = (val) => (val ? new Date(val).toLocaleDateString() : '—');
const DETAILS_MIN_COL_PX = [80, 120, 80, 150, 140, 120, 90, 120];
const UB_MIN_COL_PX      = [110, 180, 110, 170, 110];
const DETAILS_MIN_TABLE_PX = DETAILS_MIN_COL_PX.reduce((a, b) => a + b, 0);
const UB_MIN_TABLE_PX      = UB_MIN_COL_PX.reduce((a, b) => a + b, 0);

const thStyle = (widthPct, isLast, minPx) => ({
    width: `${widthPct}%`,
    minWidth: minPx ? `${minPx}px` : undefined,
    fontSize: '10.5px',
    letterSpacing: '0.4px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    borderRight: isLast ? 'none' : '1px solid #dee2e6',
});

const tdStyle = (widthPct, isLast, minPx) => ({
    width: `${widthPct}%`,
    minWidth: minPx ? `${minPx}px` : undefined,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    verticalAlign: 'top',
    borderRight: isLast ? 'none' : '1px solid #eef0f2',
});

const useIsMobile = (breakpoint = 580) => {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
    );

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [breakpoint]);

    return isMobile;
};

const renderWeekDetailSideTable = (records, sourceLabel, isAi) => (
    <table className={`table mb-0 avs-weekdetail-table ${isAi ? 'avs-weekdetail-ai' : 'avs-weekdetail-manual'}`}>
        <thead>
            <tr>
                <th>Company Name<br /><span className="avs-weekdetail-subhead">(Source - {sourceLabel})</span></th>
                <th>Bill No<br /><span className="avs-weekdetail-subhead">Description</span></th>
                <th>Verification Type<br /><span className="avs-weekdetail-subhead">Verification Date</span></th>
                <th>Created By<br /><span className="avs-weekdetail-subhead">Created Date</span></th>
            </tr>
        </thead>
        <tbody>
            {records.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted py-2">No {sourceLabel} records</td></tr>
            ) : (
                records.map((r, idx) => (
                    <tr key={`${r.verificationId || idx}`}>
                        <td>{r.companyName || '—'}</td>
                        <td>{r.billNumber || '—'}<br /><span className="avs-weekdetail-sub">{r.description || '—'}</span></td>
                        <td>{r.verificationTypeName || '—'}<br /><span className="avs-weekdetail-sub">{fmtDate(r.verificationDate)}</span></td>
                        <td>{r.createdUserName || '—'}<br /><span className="avs-weekdetail-sub">{fmtDate(r.createdDate)}</span></td>
                    </tr>
                ))
            )}
        </tbody>
    </table>
);

const WeekDetailBlock = React.memo(function WeekDetailBlock({ group, isCollapsed, onToggle }) {
    const totalCount = group.aiRecords.length + group.manualRecords.length;
    return (
        <div className="avs-weekdetail-block">
            <button
                type="button"
                className="avs-weekdetail-header avs-weekdetail-header-toggle"
                onClick={() => onToggle(group.label)}
                aria-expanded={!isCollapsed}
            >
                <span>{group.label}</span>
                <span className="avs-weekdetail-count">
                    {totalCount} record{totalCount !== 1 ? 's' : ''}
                </span>
                <ChevronDown
                    size={16}
                    className={`avs-weekdetail-chevron ${!isCollapsed ? 'avs-weekdetail-chevron-open' : ''}`}
                />
            </button>
            {!isCollapsed && (
                <div className="avs-weekdetail-tables">
                    <div className="avs-weekdetail-col">
                        {renderWeekDetailSideTable(group.aiRecords, 'AI', true)}
                    </div>
                    <div className="avs-weekdetail-col">
                        {renderWeekDetailSideTable(group.manualRecords, 'Manual', false)}
                    </div>
                </div>
            )}
        </div>
    );
});

const AIVerificationSummaryReport = () => {
    const { domain } = useCurrentDomain();
    const isMobile = useIsMobile(580);
    const [isChartMobile, setIsChartMobile] = useState(false);

    const [activeTab, setActiveTab] = useState('Summary');
    const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 35); return d; });
    const [toDate, setToDate]     = useState(new Date());
    const [isExcelHovered, setIsExcelHovered] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const [summary, setSummary] = useState({ data: [], loading: false, loaded: false });

    const [details, setDetails] = useState({
        records: [], totalCount: 0, pageNumber: 1, pageSize: 50, loading: false, loaded: false,
    });
    const [detailsColWidths, setDetailsColWidths] = useState([9.1, 13.2, 9.1, 19, 14, 13.2, 9.1, 13.3]);
    const detailsTableRef = useRef(null);

    const [userBreakdown, setUserBreakdown] = useState({
        records: [], totalCount: 0, pageNumber: 1, pageSize: 50, loading: false, loaded: false,
    });
    const [ubColWidths, setUbColWidths] = useState([15.4, 28.2, 13, 28, 15.4]);
    const ubTableRef = useRef(null);

    const [detailsViewMode, setDetailsViewMode] = useState('summary');
    const [ubViewMode, setUbViewMode] = useState('summary');
    const [summaryViewMode, setSummaryViewMode] = useState('weekly');
    const [pivotLoading, setPivotLoading] = useState(false);
    const [weekBuckets, setWeekBuckets] = useState([]);
    const [billPivotRows, setBillPivotRows] = useState([]);
    const [userPivotRows, setUserPivotRows] = useState([]);
    const [weeklyBillDetailGroups, setWeeklyBillDetailGroups] = useState([]);
    const [expandedBillGroups, setExpandedBillGroups] = useState(new Set());
    const [expandedUserGroups, setExpandedUserGroups] = useState(new Set());
    const [collapsedWeekBlocks, setCollapsedWeekBlocks] = useState(new Set());
    const [trendLoading, setTrendLoading] = useState(false);
    const [trendData, setTrendData] = useState([]);

    const PIVOT_PAGE_SIZE = 100;
    const [billPivotPage, setBillPivotPage] = useState(1);
    const [userPivotPage, setUserPivotPage] = useState(1);

    const [, startTransition] = useTransition();

    const dirtyTabs = useRef({ Summary: true, Details: true, UserBreakdown: true, Pivot: true });

    useEffect(() => {
        const checkChartMobile = () => setIsChartMobile(window.innerWidth < CHART_MOBILE_BREAKPOINT);
        checkChartMobile();
        window.addEventListener('resize', checkChartMobile);
        return () => window.removeEventListener('resize', checkChartMobile);
    }, []);

    const { handleMouseDown: handleDetailsMouseDown } = useColumnResizePct(
        detailsColWidths, setDetailsColWidths, detailsTableRef
    );
    const { handleMouseDown: handleUbMouseDown } = useColumnResizePct(
        ubColWidths, setUbColWidths, ubTableRef
    );

    const fetchTab = useCallback(async (tabName, page, size, isNewSearch) => {
        if (tabName === 'Summary') {
            setSummary((s) => ({ ...s, loading: true }));
        } else if (tabName === 'Details') {
            setDetails((d) => ({ ...d, loading: true }));
        } else {
            setUserBreakdown((u) => ({ ...u, loading: true }));
        }
        if (isNewSearch) setSearchLoading(true);

        try {
            const payload = {
                Domain: domain,
                FromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : '',
                ToDate: toDate ? moment(toDate).format("YYYY-MM-DD") : '',
                VerificationSource: 'All',
                TabName: tabName,
                PageNumber: page,
                PageSize: size,
                IsExport: false,
            };
            const res = await getAIVerificationSummaryReport(payload);

            if (tabName === 'Summary') {
                setSummary({ data: res.countSummary || res.CountSummary || [], loading: false, loaded: true });
            } else if (tabName === 'Details') {
                setDetails({
                    records: res.details || res.Details || [],
                    totalCount: res.totalRecords ?? res.TotalRecords ?? 0,
                    pageNumber: page,
                    pageSize: size,
                    loading: false,
                    loaded: true,
                });
            } else {
                setUserBreakdown({
                    records: res.userBreakdown || res.userBreakdown || [],
                    totalCount: res.totalRecords ?? 0,
                    pageNumber: page,
                    pageSize: size,
                    loading: false,
                    loaded: true,
                });
            }
            dirtyTabs.current[tabName] = false;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load report data');
            console.error(err);
            if (tabName === 'Summary') setSummary((s) => ({ ...s, loading: false }));
            else if (tabName === 'Details') setDetails((d) => ({ ...d, loading: false }));
            else setUserBreakdown((u) => ({ ...u, loading: false }));
        } finally {
            if (isNewSearch) setSearchLoading(false);
        }
    }, [domain, fromDate, toDate]);

    const buildWeeklyBuckets = (rangeFrom, rangeTo) => {
        if (!rangeFrom || !rangeTo) return [];
        const today = moment().endOf('day');
        const effectiveTo = moment(rangeTo).isAfter(today) ? today : moment(rangeTo);
        if (moment(rangeFrom).startOf('day').isAfter(effectiveTo)) return [];

        const weeks = [];
        let cursor = moment(effectiveTo).startOf('week');
        const firstWeekStart = moment(rangeFrom).startOf('week');
        const clampFrom = moment(rangeFrom).startOf('day');
        let safety = 0;
        while (cursor.isSameOrAfter(firstWeekStart) && safety < 3000) {
            const start = cursor.clone();
            const end = cursor.clone().endOf('week');

            const displayStart = start.isBefore(clampFrom) ? clampFrom.clone() : start;
            const displayEnd = end.isAfter(effectiveTo) ? effectiveTo.clone() : end;
            weeks.push({
                start,
                end,
                key: start.format('YYYY-MM-DD'),
                label: `${displayEnd.format('MM/DD/YY')} - ${displayStart.format('MM/DD/YY')}`,
                shortLabel: start.format('MM/DD'),
            });
            cursor = cursor.clone().subtract(7, 'days');
            safety += 1;
        }
        return weeks;
    };

    const buildWeekLookup = (weeks) => {
        return weeks.map((w) => ({
            ...w,
            startMs: w.start.valueOf(),
            endMs:   w.end.valueOf(),
        }));
    };

    const findWeekFast = (weeksWithMs, tsMs) => {
        for (let i = 0; i < weeksWithMs.length; i++) {
            if (tsMs >= weeksWithMs[i].startMs && tsMs <= weeksWithMs[i].endMs) return weeksWithMs[i];
        }
        return null;
    };

    const buildBillPivotData = (allRecords, weeks) => {
        const weeksWithMs = buildWeekLookup(weeks);
        const map = new Map();
        (allRecords || []).forEach((row) => {
            const company = row.companyName || 'Unknown';
            const billNo = row.billNumber || 'Unknown';
            const key = `${company}||${billNo}`;
            const isAI = (row.verificationSource || '').toUpperCase() === 'AI MODULE';

            if (!map.has(key)) {
                const weekly = {};
                weeks.forEach((w) => { weekly[w.label] = { ai: 0, manual: 0 }; });
                map.set(key, { companyName: company, billNumber: billNo, total: { ai: 0, manual: 0 }, weekly });
            }
            const entry = map.get(key);
            if (isAI) entry.total.ai += 1; else entry.total.manual += 1;

            if (row.createdDate) {
                const tsMs = new Date(row.createdDate).getTime();
                const week = findWeekFast(weeksWithMs, tsMs);
                if (week) {
                    if (isAI) entry.weekly[week.label].ai += 1;
                    else entry.weekly[week.label].manual += 1;
                }
            }
        });
        return Array.from(map.values()).sort((a, b) =>
            a.companyName.localeCompare(b.companyName) || String(a.billNumber).localeCompare(String(b.billNumber))
        );
    };

    const buildUserPivotData = (allRecords, weeks) => {
        const weeksWithMs = buildWeekLookup(weeks);
        const map = new Map();
        (allRecords || []).forEach((row) => {
            const company = row.companyName || 'Unknown';
            const user = row.createdUserName || 'Unknown';
            const key = `${company}||${user}`;
            const isAI = (row.verificationSource || '').toUpperCase() === 'AI MODULE';

            if (!map.has(key)) {
                const weekly = {};
                weeks.forEach((w) => { weekly[w.label] = { ai: 0, manual: 0 }; });
                map.set(key, { companyName: company, userName: user, total: { ai: 0, manual: 0 }, weekly });
            }
            const entry = map.get(key);
            if (isAI) entry.total.ai += 1; else entry.total.manual += 1;

            if (row.createdDate) {
                const tsMs = new Date(row.createdDate).getTime();
                const week = findWeekFast(weeksWithMs, tsMs);
                if (week) {
                    if (isAI) entry.weekly[week.label].ai += 1;
                    else entry.weekly[week.label].manual += 1;
                }
            }
        });
        return Array.from(map.values()).sort((a, b) =>
            a.companyName.localeCompare(b.companyName) || a.userName.localeCompare(b.userName)
        );
    };

    const buildTrendWeeklyData = (allRecords, weeks) => {
        const weeksWithMs = buildWeekLookup(weeks);
        const byKey = new Map(weeks.map((w) => [w.key, { label: w.label, shortLabel: w.shortLabel, ai: 0, manual: 0 }]));
        (allRecords || []).forEach((row) => {
            if (!row.createdDate) return;
            const tsMs = new Date(row.createdDate).getTime();
            const week = findWeekFast(weeksWithMs, tsMs);
            if (!week) return;
            const bucket = byKey.get(week.key);
            const isAI = (row.verificationSource || '').toUpperCase() === 'AI MODULE';
            if (isAI) bucket.ai += 1; else bucket.manual += 1;
        });
        return weeks.map((w) => byKey.get(w.key));
    };

    const buildWeeklyDetailGroups = (allRecords, weeks) => {
        const weeksWithMs = buildWeekLookup(weeks);
        const buckets = new Map(weeks.map((w) => [w.label, { ai: [], manual: [] }]));

        const sortFn = (a, b) =>
            (a.companyName || '').localeCompare(b.companyName || '') ||
            String(a.billNumber || '').localeCompare(String(b.billNumber || ''));

        (allRecords || []).forEach((row) => {
            if (!row.createdDate) return;
            const tsMs = new Date(row.createdDate).getTime();
            const week = findWeekFast(weeksWithMs, tsMs);
            if (!week) return;
            const bucket = buckets.get(week.label);
            if ((row.verificationSource || '').toUpperCase() === 'AI MODULE') {
                bucket.ai.push(row);
            } else {
                bucket.manual.push(row);
            }
        });

        return weeks.map((w) => {
            const b = buckets.get(w.label);
            return {
                label: w.label,
                aiRecords:     b.ai.sort(sortFn),
                manualRecords: b.manual.sort(sortFn),
            };
        });
    };

    const fetchWeeklySummaryAndTrend = useCallback(async (isNewSearch) => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        if (isNewSearch) setSearchLoading(true);
        setPivotLoading(true);
        setTrendLoading(true);
        try {
            const weeks = buildWeeklyBuckets(fromDate, toDate);
            setWeekBuckets(weeks);

            const payload = {
                Domain: domain,
                FromDate: moment(fromDate).format('YYYY-MM-DD'),
                ToDate: moment(toDate).format('YYYY-MM-DD'),
                VerificationSource: 'All',
                TabName: 'Details',
                IsExport: true,
            };
            const res = await getAIVerificationSummaryReport(payload);
            const allRecords = res.details || res.Details || [];

            const billRows  = buildBillPivotData(allRecords, weeks);
            const userRows  = buildUserPivotData(allRecords, weeks);
            const detGroups = buildWeeklyDetailGroups(allRecords, weeks);

            setBillPivotRows(billRows);
            setUserPivotRows(userRows);
            setWeeklyBillDetailGroups(detGroups);
            setCollapsedWeekBlocks(new Set(weeks.map((w) => w.label)));
            setBillPivotPage(1);
            setUserPivotPage(1);

            const trendWeeks = weeks.slice(0, TREND_WEEKS_BACK);
            setTrendData(buildTrendWeeklyData(allRecords, trendWeeks));

            dirtyTabs.current.Pivot = false;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to load weekly summary');
            console.error(err);
        } finally {
            setPivotLoading(false);
            setTrendLoading(false);
            if (isNewSearch) setSearchLoading(false);
        }
    }, [domain, fromDate, toDate]);

    useEffect(() => {
        if (!domain) return;
        dirtyTabs.current = { Summary: true, Details: true, UserBreakdown: true, Pivot: true };
        fetchForActiveTab(true);
    }, [domain]);

    const fetchForActiveTab = useCallback((isNewSearch) => {
        if (activeTab === 'Summary') {
            if (summaryViewMode === 'weekly') {
                if (isNewSearch || dirtyTabs.current.Pivot) fetchWeeklySummaryAndTrend(isNewSearch);
            } else if (isNewSearch || dirtyTabs.current.Summary) {
                fetchTab('Summary', 1, 25, isNewSearch);
            }
        } else if (activeTab === 'Details') {
            if (detailsViewMode === 'summary') {
                if (isNewSearch || dirtyTabs.current.Pivot) fetchWeeklySummaryAndTrend(isNewSearch);
            } else if (isNewSearch || dirtyTabs.current.Details) {
                fetchTab('Details', 1, details.pageSize, isNewSearch);
            }
        } else if (activeTab === 'UserBreakdown') {
            if (ubViewMode === 'summary') {
                if (isNewSearch || dirtyTabs.current.Pivot) fetchWeeklySummaryAndTrend(isNewSearch);
            } else if (isNewSearch || dirtyTabs.current.UserBreakdown) {
                fetchTab('UserBreakdown', 1, userBreakdown.pageSize, isNewSearch);
            }
        }
    }, [activeTab, summaryViewMode, detailsViewMode, ubViewMode, details.pageSize, userBreakdown.pageSize, fetchTab, fetchWeeklySummaryAndTrend]);

    useEffect(() => {
        if (!domain) return;
        fetchForActiveTab(false);
    }, [activeTab]);

    useEffect(() => {
        if (!fromDate || !toDate) return;

        dirtyTabs.current = { Summary: true, Details: true, UserBreakdown: true, Pivot: true };
        fetchForActiveTab(true);
    }, [fromDate, toDate]);

    const handleSearch = () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        dirtyTabs.current = { Summary: true, Details: true, UserBreakdown: true, Pivot: true };
        setDetails((d) => ({ ...d, pageNumber: 1 }));
        setUserBreakdown((u) => ({ ...u, pageNumber: 1 }));
        fetchForActiveTab(true);
    };


    const handleSummaryViewModeChange = (mode) => {
        if (mode === summaryViewMode) return;
        setSummaryViewMode(mode);
        if (!fromDate || !toDate) return;
        if (mode === 'weekly') {
            if (dirtyTabs.current.Pivot) fetchWeeklySummaryAndTrend(false);
        } else if (dirtyTabs.current.Summary) {
            fetchTab('Summary', 1, 25, false);
        }
    };

    const handleDetailsViewModeChange = (mode) => {
        if (mode === detailsViewMode) return;
        setDetailsViewMode(mode);
        if (!fromDate || !toDate) return;
        if (mode === 'summary') {
            setPivotLoading(true);
            fetchWeeklySummaryAndTrend(false);
        } else {
            setDetails((d) => ({ ...d, loading: true, records: [] }));
            fetchTab('Details', 1, details.pageSize, false);
        }
    };

    const handleUbViewModeChange = (mode) => {
        if (mode === ubViewMode) return;
        setUbViewMode(mode);
        if (!fromDate || !toDate) return;
        if (mode === 'summary') {
            setPivotLoading(true);
            fetchWeeklySummaryAndTrend(false);
        } else {
            setUserBreakdown((u) => ({ ...u, loading: true, records: [] }));
            fetchTab('UserBreakdown', 1, userBreakdown.pageSize, false);
        }
    };

    const tabsRef = useRef(null);
    const scrollTabs = (direction) => {
        if (!tabsRef.current) return;
        tabsRef.current.scrollBy({
            left: direction === "left" ? -150 : 150,
            behavior: "smooth"
        });
    };

    const goToPage = (tabName, page) => {
        const tabState = tabName === 'Details' ? details : userBreakdown;
        const totalPages = Math.max(1, Math.ceil(tabState.totalCount / tabState.pageSize));
        if (page < 1 || page > totalPages || tabState.loading) return;
        fetchTab(tabName, page, tabState.pageSize, false);
    };

    const handlePageSizeChange = (tabName, opt) => {
        fetchTab(tabName, 1, opt.value, false);
    };

    const toggleBillGroupExpand = (key) => {
        setExpandedBillGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };
    const toggleUserGroupExpand = (key) => {
        setExpandedUserGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    const toggleWeekBlockCollapse = useCallback((label) => {
        setCollapsedWeekBlocks((prev) => {
            const next = new Set(prev);
            if (next.has(label)) next.delete(label); else next.add(label);
            return next;
        });
    }, []);

    const billGrandTotal = useMemo(() => billPivotRows.reduce((acc, row) => {
        acc.total.ai += row.total.ai;
        acc.total.manual += row.total.manual;
        weekBuckets.forEach((w) => {
            if (!acc.weekly[w.label]) acc.weekly[w.label] = { ai: 0, manual: 0 };
            acc.weekly[w.label].ai += row.weekly[w.label]?.ai || 0;
            acc.weekly[w.label].manual += row.weekly[w.label]?.manual || 0;
        });
        return acc;
    }, { total: { ai: 0, manual: 0 }, weekly: {} }), [billPivotRows, weekBuckets]);

    const userGrandTotal = useMemo(() => userPivotRows.reduce((acc, row) => {
        acc.total.ai += row.total.ai;
        acc.total.manual += row.total.manual;
        weekBuckets.forEach((w) => {
            if (!acc.weekly[w.label]) acc.weekly[w.label] = { ai: 0, manual: 0 };
            acc.weekly[w.label].ai += row.weekly[w.label]?.ai || 0;
            acc.weekly[w.label].manual += row.weekly[w.label]?.manual || 0;
        });
        return acc;
    }, { total: { ai: 0, manual: 0 }, weekly: {} }), [userPivotRows, weekBuckets]);

    const renderCount = (value) => (
        value === 0 ? <span className="avs-zero-value">0</span> : value
    );

    // ---------- Excel export ----------
    const exportPivotToExcel = (rows, grandTotal, extraColLabel, extraColKey, fileSuffix, sheetName) => {
        if (!rows.length) { toast.error('No data to export'); return; }
        const tid = toast.loading('Preparing export...');
        try {
            const numWeekCols = weekBuckets.length * 2;
            const totalCols = 5 + numWeekCols;

            const headerRow1 = new Array(totalCols).fill('');
            headerRow1[0] = 'Sr.no';
            headerRow1[1] = 'Company Name';
            headerRow1[2] = extraColLabel;
            headerRow1[3] = 'Total';
            weekBuckets.forEach((w, i) => { headerRow1[5 + i * 2] = w.label; });

            const headerRow2 = new Array(totalCols).fill('');
            headerRow2[3] = 'AI Module';
            headerRow2[4] = 'Manual';
            weekBuckets.forEach((w, i) => {
                headerRow2[5 + i * 2] = 'AI Module';
                headerRow2[6 + i * 2] = 'Manual';
            });

            const dataRows = rows.map((row, idx) => {
                const r = [idx + 1, row.companyName, row[extraColKey], row.total.ai, row.total.manual];
                weekBuckets.forEach((w) => {
                    r.push(row.weekly[w.label]?.ai || 0);
                    r.push(row.weekly[w.label]?.manual || 0);
                });
                return r;
            });

            const grandTotalRow = ['Grand Total', '', '', grandTotal.total.ai, grandTotal.total.manual];
            weekBuckets.forEach((w) => {
                grandTotalRow.push(grandTotal.weekly[w.label]?.ai || 0);
                grandTotalRow.push(grandTotal.weekly[w.label]?.manual || 0);
            });

            const aoa = [headerRow1, headerRow2, ...dataRows, grandTotalRow];
            const ws = XLSX.utils.aoa_to_sheet(aoa);

            const lastRowIdx = aoa.length - 1;
            const merges = [
                { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
                { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
                { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
                { s: { r: 0, c: 3 }, e: { r: 0, c: 4 } },
                { s: { r: lastRowIdx, c: 0 }, e: { r: lastRowIdx, c: 2 } },
            ];
            weekBuckets.forEach((_, i) => {
                merges.push({ s: { r: 0, c: 5 + i * 2 }, e: { r: 0, c: 6 + i * 2 } });
            });
            ws['!merges'] = merges;

            ws['!cols'] = [
                { wch: 6 }, { wch: 26 }, { wch: 18 }, { wch: 10 }, { wch: 10 },
                ...weekBuckets.flatMap(() => [{ wch: 11 }, { wch: 11 }]),
            ];

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, sheetName);
            XLSX.writeFile(wb, `AIVerificationSummary_${fileSuffix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${rows.length} row${rows.length !== 1 ? 's' : ''}`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const exportWeeklyBillDetailToExcel = () => {
        if (!weeklyBillDetailGroups.length) { toast.error('No data to export'); return; }
        const tid = toast.loading('Preparing export...');
        try {
            const rows = [];
            weeklyBillDetailGroups.forEach((group) => {
                const pushRecords = (records, sourceLabel) => {
                    records.forEach((r) => {
                        rows.push({
                            WEEK: group.label,
                            SOURCE: sourceLabel,
                            COMPANY_NAME: r.companyName || '',
                            BILL_NUMBER: r.billNumber || '',
                            DESCRIPTION: r.description || '',
                            VERIFICATION_TYPE: r.verificationTypeName || '',
                            VERIFICATION_DATE: r.verificationDate ? new Date(r.verificationDate).toLocaleString() : '',
                            CREATED_BY: r.createdUserName || '',
                            CREATED_DATE: r.createdDate ? new Date(r.createdDate).toLocaleString() : '',
                        });
                    });
                };
                pushRecords(group.aiRecords, 'AI Module');
                pushRecords(group.manualRecords, 'Manual');
            });

            if (!rows.length) { toast.error('No data to export'); return; }

            const ws = XLSX.utils.json_to_sheet(rows);
            ws['!cols'] = [
                { wch: 18 }, { wch: 10 }, { wch: 22 }, { wch: 12 }, { wch: 24 },
                { wch: 22 }, { wch: 18 }, { wch: 16 }, { wch: 18 },
            ];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'BillWiseDetail');
            XLSX.writeFile(wb, `AIVerificationSummary_BillWiseDetail_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${rows.length} row${rows.length !== 1 ? 's' : ''}`);
        } catch (err) {
            console.error(err);
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const exportBillPivotToExcel = () => exportPivotToExcel(
        billPivotRows, billGrandTotal, 'Bill No', 'billNumber', 'BillWise_WeeklySummary', 'BillWiseWeeklySummary'
    );
    const exportUserPivotToExcel = () => exportPivotToExcel(
        userPivotRows, userGrandTotal, 'User Name', 'userName', 'UserWise_WeeklySummary', 'UserWiseWeeklySummary'
    );

    const exportToExcel = async () => {
        if (!fromDate || !toDate) {
            toast.error('Please select date range');
            return;
        }
        if (activeTab === 'Details' && detailsViewMode === 'summary') {
            exportWeeklyBillDetailToExcel();
            return;
        }
        if (activeTab === 'UserBreakdown' && ubViewMode === 'summary') {
            exportUserPivotToExcel();
            return;
        }
        if (activeTab === 'Summary' && summaryViewMode === 'weekly') {
            if (!weekBuckets.length) { toast.error('No data to export'); return; }
            const tid = toast.loading('Preparing export...');
            try {
                const exportData = weekBuckets.map((w) => {
                    const wk = billGrandTotal.weekly[w.label] || { ai: 0, manual: 0 };
                    return { WEEK: w.label, AI_MODULE: wk.ai, MANUAL: wk.manual, TOTAL: wk.ai + wk.manual };
                });
                exportData.push({
                    WEEK: 'Grand Total',
                    AI_MODULE: billGrandTotal.total.ai,
                    MANUAL: billGrandTotal.total.manual,
                    TOTAL: billGrandTotal.total.ai + billGrandTotal.total.manual,
                });
                const ws = XLSX.utils.json_to_sheet(exportData);
                ws['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 10 }];
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'WeeklySummary');
                XLSX.writeFile(wb, `AIVerificationSummary_Weekly_${new Date().toISOString().slice(0, 10)}.xlsx`);
                toast.success(`Exported ${exportData.length} row${exportData.length !== 1 ? 's' : ''}`);
            } catch (err) {
                console.error(err);
                toast.error('Failed to export');
            } finally {
                toast.dismiss(tid);
            }
            return;
        }

        const tid = toast.loading('Preparing export...');
        try {
            const payload = {
                Domain: domain,
                FromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : '',
                ToDate: toDate ? moment(toDate).format("YYYY-MM-DD") : '',
                TabName: activeTab,
                IsExport: true,
            };
            const data = await getAIVerificationSummaryReport(payload);

            let exportData = [];
            let sheetName = activeTab;

            if (activeTab === 'Summary') {
                exportData = (data.countSummary || data.CountSummary || []).map((row) => ({
                    VERIFICATION_TYPE: row.verificationType || '',
                    COUNT: row.count ?? '',
                }));
            } else if (activeTab === 'Details') {
                exportData = (data.details || data.Details || []).map((row) => ({
                    VERIFICATION_SOURCE: row.verificationSource || 'All',
                    COMPANY_NAME:        row.companyName || '',
                    BILL_NUMBER:         row.billNumber || '',
                    VERIFICATION_TYPE:   row.verificationTypeName || '',
                    DESCRIPTION:         row.description || '',
                    VERIFICATION_DATE:   row.verificationDate ? new Date(row.verificationDate).toLocaleString() : '',
                    CREATED_BY:          row.createdUserName || '',
                    CREATED_EMAIL:       row.createdUserEmail || '',
                    CREATED_DATE:        row.createdDate ? new Date(row.createdDate).toLocaleString() : '',
                }));
            } else {
                exportData = (data.userBreakdown || data.UserBreakdown || []).map((row) => ({
                    VERIFICATION_SOURCE: row.verificationSource || '',
                    COMPANY_NAME:        row.companyName || '',
                    USER_NAME:           row.userName || '',
                    EMAIL:               row.email || '',
                    VERIFICATION_COUNT:  row.verificationCount ?? '',
                }));
            }

            if (!exportData.length) { toast.error('No data to export'); return; }

            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
            XLSX.writeFile(wb, `AIVerificationSummary_${activeTab}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success(`Exported ${exportData.length} record${exportData.length !== 1 ? 's' : ''}`);
        } catch {
            toast.error('Failed to export');
        } finally {
            toast.dismiss(tid);
        }
    };

    const filterLabelStyle = { fontSize: '10px', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '3px', display: 'block' };

    const renderPagination = (tabName, tabState) => {
        const totalPages = Math.max(1, Math.ceil(tabState.totalCount / tabState.pageSize));
        const rangeStart = tabState.totalCount === 0 ? 0 : (tabState.pageNumber - 1) * tabState.pageSize + 1;
        const rangeEnd   = Math.min(tabState.pageNumber * tabState.pageSize, tabState.totalCount);

        if (tabState.loading || tabState.records.length === 0) return null;

        return (
            <div
                className="avs-pagination-bar"
                style={{
                    padding: '10px 20px', borderTop: '1px solid #e5e7eb',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#fafafa', flexWrap: 'wrap', gap: '8px',
                }}
            >
                <span className="avs-pagination-summary" style={{ fontSize: 12, color: '#6b7280' }}>
                    {tabState.totalCount === 0 ? 'No records' : <>Showing {rangeStart}–{rangeEnd} of {tabState.totalCount}</>}
                </span>

                <div className="avs-pagination-controls" style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <button
                        onClick={() => goToPage(tabName, tabState.pageNumber - 1)}
                        disabled={tabState.pageNumber === 1 || tabState.loading}
                        style={{
                            padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                            background: tabState.pageNumber === 1 ? '#f3f4f6' : '#fff',
                            color: tabState.pageNumber === 1 ? '#9ca3af' : BRAND_GREEN,
                            cursor: tabState.pageNumber === 1 ? 'not-allowed' : 'pointer', fontSize: 12
                        }}
                    >‹ Prev</button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => {
                            if (totalPages <= 7) return true;
                            if (p === 1 || p === totalPages) return true;
                            if (p >= tabState.pageNumber - 2 && p <= tabState.pageNumber + 2) return true;
                            return false;
                        })
                        .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                            acc.push(p);
                            return acc;
                        }, [])
                        .map((p, idx) =>
                            p === '...' ? (
                                <span key={`ellipsis-${idx}`} style={{ color: '#9ca3af', fontSize: 12, padding: '0 2px' }}>...</span>
                            ) : (
                                <button
                                    key={p}
                                    onClick={() => goToPage(tabName, p)}
                                    disabled={tabState.loading}
                                    style={{
                                        width: 28, height: 28, borderRadius: 6, border: '1px solid #d1fae5',
                                        background: tabState.pageNumber === p ? BRAND_GREEN : '#fff',
                                        color: tabState.pageNumber === p ? '#fff' : BRAND_GREEN,
                                        fontWeight: tabState.pageNumber === p ? 700 : 400,
                                        cursor: 'pointer', fontSize: 12
                                    }}
                                >
                                    {tabState.loading && p === tabState.pageNumber
                                        ? <RefreshCw size={12} style={{ animation: 'avs-spin 1s linear infinite' }} />
                                        : p}
                                </button>
                            )
                        )}

                    <button
                        onClick={() => goToPage(tabName, tabState.pageNumber + 1)}
                        disabled={tabState.pageNumber === totalPages || tabState.loading}
                        style={{
                            padding: '4px 8px', borderRadius: 6, border: '1px solid #d1fae5',
                            background: tabState.pageNumber === totalPages ? '#f3f4f6' : '#fff',
                            color: tabState.pageNumber === totalPages ? '#9ca3af' : BRAND_GREEN,
                            cursor: tabState.pageNumber === totalPages ? 'not-allowed' : 'pointer', fontSize: 12
                        }}
                    >Next ›</button>
                </div>

                <div className="avs-pagesize-select" style={{ width: '110px' }}>
                    <Select
                        options={pageSizeSelectOptions}
                        value={pageSizeSelectOptions.find((o) => o.value === tabState.pageSize)}
                        onChange={(opt) => handlePageSizeChange(tabName, opt)}
                        isSearchable={false}
                        isDisabled={tabState.loading}
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        classNamePrefix="react-select"
                    />
                </div>
            </div>
        );
    };

    const renderSummaryTotalView = () => {
        if (summary.loading) {
            return (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <RefreshCw size={18} style={{ animation: 'avs-spin 1s linear infinite', color: BRAND_GREEN }} />
                </div>
            );
        }
        if (!summary.data.length) {
            return <div className="text-center py-5 text-muted">No summary data for the selected range.</div>;
        }
        return (
            <div className="avs-summary-wrap">
                {summary.data.map((row, idx) => {
                    const isAiRow =
                        (row.verificationType || '').toLowerCase() === 'ai module';

                    return (
                        <div
                            key={idx}
                            className={`avs-summary-card ${isAiRow ? 'avs-summary-accent' : ''}`}
                        >
                            <div
                                className="avs-summary-card-label"
                                style={{
                                    color: isAiRow ? BRAND_GREEN : '#374151',
                                    fontWeight: 700,
                                    fontSize: '15px',
                                }}
                            >
                                {row.verificationType}
                            </div>

                            <div className="avs-summary-card-value">
                                {row.count ?? 0}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderSummaryWeeklyView = () => (
        <>
            <div className="table-responsive avs-summary-table-wrap avs-summary-desktop-table" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {pivotLoading ? (
                    <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                        <RefreshCw size={14} style={{ animation: 'avs-spin 1s linear infinite' }} />
                        Loading summary...
                    </div>
                ) : weekBuckets.length === 0 ? (
                    <div className="text-center py-4 text-muted">No matching records found.</div>
                ) : (
                    <table className="table table-bordered align-middle mb-0 avs-summary-table">
                        <thead className="sticky-top" style={{ zIndex: 10 }}>
                            <tr>
                                <th colSpan={2} className="text-center avs-total-header">Total</th>
                                {weekBuckets.map((w, i) => (
                                    <th key={w.label} colSpan={2} className={`text-center avs-week-header ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{w.label}</th>
                                ))}
                            </tr>
                            <tr>
                                <th className="text-center avs-total-col-header avs-col-ai">AI Module</th>
                                <th className="text-center avs-total-col-header avs-col-manual">Manual</th>
                                {weekBuckets.map((w, i) => (
                                    <React.Fragment key={`${w.label}-sub`}>
                                        <th className={`text-center avs-week-divider avs-col-ai ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>AI Module</th>
                                        <th className={`text-center avs-col-manual ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>Manual</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="text-center avs-total-col avs-col-ai">{renderCount(billGrandTotal.total.ai)}</td>
                                <td className="text-center avs-total-col avs-col-manual">{renderCount(billGrandTotal.total.manual)}</td>
                                {weekBuckets.map((w, i) => {
                                    const wk = billGrandTotal.weekly[w.label] || { ai: 0, manual: 0 };
                                    return (
                                        <React.Fragment key={`overall-${w.label}`}>
                                            <td className={`text-center avs-week-divider avs-col-ai ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{renderCount(wk.ai)}</td>
                                            <td className={`text-center avs-col-manual ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{renderCount(wk.manual)}</td>
                                        </React.Fragment>
                                    );
                                })}
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>

            <div className="avs-summary-mobile-cards">
                {pivotLoading ? (
                    <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                        <RefreshCw size={14} style={{ animation: 'avs-spin 1s linear infinite' }} />
                        Loading summary...
                    </div>
                ) : weekBuckets.length === 0 ? (
                    <div className="text-center py-4 text-muted">No matching records found.</div>
                ) : (
                    <div className="avs-scard avs-scard-total">
                        <div className="avs-scard-stats">
                            <div className="avs-scard-stat avs-scard-stat-ai">
                                <span className="avs-scard-stat-value">{billGrandTotal.total.ai}</span>
                                <span className="avs-scard-stat-label">AI Module</span>
                            </div>
                            <div className="avs-scard-stat avs-scard-stat-manual">
                                <span className="avs-scard-stat-value">{billGrandTotal.total.manual}</span>
                                <span className="avs-scard-stat-label">Manual</span>
                            </div>
                        </div>
                        <div className="avs-scard-weeks">
                            {weekBuckets.map((w) => {
                                const wk = billGrandTotal.weekly[w.label] || { ai: 0, manual: 0 };
                                return (
                                    <div key={w.label} className="avs-scard-week-row">
                                        <span className="avs-scard-week-label">{w.label}</span>
                                        <span className="avs-scard-week-counts">
                                            <span className="avs-scard-week-ai">{renderCount(wk.ai)} AI</span>
                                            <span className="avs-scard-week-sep">·</span>
                                            <span className="avs-scard-week-manual">{renderCount(wk.manual)} Manual</span>
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {renderTrendChart()}
        </>
    );

    const renderSummaryTab = () => (
        <>
            <div className="d-flex justify-content-end px-2 pt-2">
                <div className="btn-group avs-view-toggle" role="group" style={{ height: '30px' }}>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: '12px', height: '30px',
                            backgroundColor: summaryViewMode === 'weekly' ? BRAND_GREEN : '#fff',
                            color: summaryViewMode === 'weekly' ? '#fff' : '#333',
                            border: '1px solid #dee2e6',
                        }}
                        onClick={() => handleSummaryViewModeChange('weekly')}
                        title="Weekly breakdown"
                    >Weekly View</button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: '12px', height: '30px',
                            backgroundColor: summaryViewMode === 'total' ? BRAND_GREEN : '#fff',
                            color: summaryViewMode === 'total' ? '#fff' : '#333',
                            border: '1px solid #dee2e6', borderLeft: 'none',
                        }}
                        onClick={() => handleSummaryViewModeChange('total')}
                        title="Total for selected range"
                    >Total View</button>
                </div>
            </div>
            {summaryViewMode === 'weekly' ? renderSummaryWeeklyView() : renderSummaryTotalView()}
        </>
    );

    const renderTrendChart = () => (
        <div className="avs-trend-card">
            <div className="px-3 pt-3 pb-1 d-flex align-items-center justify-content-between avs-trend-header">
                <div>
                    <div className="avs-trend-title">
                        Last {trendData.length || TREND_WEEKS_BACK} Week{(trendData.length || TREND_WEEKS_BACK) === 1 ? '' : 's'} Trend
                    </div>
                    <div className="avs-trend-subtitle">AI Module vs Manual verifications, by week</div>
                </div>
                {trendLoading && <RefreshCw size={14} style={{ animation: 'avs-spin 1s linear infinite', color: '#6c757d' }} />}
            </div>
            <div className="px-2 pb-3 avs-trend-chart-area" style={{ height: 320 }}>
                {trendLoading ? (
                    <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 12.5 }}>
                        Loading trend...
                    </div>
                ) : trendData.length === 0 ? (
                    <div className="h-100 d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 12.5 }}>
                        No data available.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={trendData}
                            margin={isChartMobile ? { top: 8, right: 4, left: -20, bottom: 0 } : { top: 8, right: 16, left: 0, bottom: 0 }}
                            barGap={0}
                            barCategoryGap={isChartMobile ? '12%' : '20%'}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef1f0" />
                            <XAxis
                                dataKey={isChartMobile ? 'shortLabel' : 'label'}
                                tick={{ fontSize: isChartMobile ? 10.5 : 12, fill: '#6c757d' }}
                                axisLine={{ stroke: '#e5e7eb' }}
                                tickLine={false}
                                interval={0}
                            />
                            <YAxis
                                allowDecimals={false}
                                width={isChartMobile ? 28 : 36}
                                tick={{ fontSize: isChartMobile ? 10.5 : 12, fill: '#6c757d' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                labelFormatter={(value, payload) => (payload && payload[0] ? payload[0].payload.label : value)}
                                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                                labelStyle={{ fontWeight: 600, color: '#33403c' }}
                            />
                            <Legend
                                wrapperStyle={{ fontSize: isChartMobile ? 11 : 12 }}
                                iconType="circle"
                                iconSize={isChartMobile ? 8 : 10}
                            />
                            <Bar dataKey="ai" name="AI Module" fill={BRAND_GREEN} radius={[4, 4, 0, 0]} maxBarSize={isChartMobile ? 22 : 40} />
                            <Bar dataKey="manual" name="Manual" fill="#94a3b8" radius={[4, 4, 0, 0]} maxBarSize={isChartMobile ? 22 : 40} />
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );

    const renderPivotView = ({ rows, grandTotal, extraColLabel, extraColKey, expandedGroups, toggleExpand, groupKeyOf, page, setPage }) => {
        const totalRows   = rows.length;
        const totalPages  = Math.max(1, Math.ceil(totalRows / PIVOT_PAGE_SIZE));
        const safePage    = Math.min(page, totalPages);
        const pageStart   = (safePage - 1) * PIVOT_PAGE_SIZE;
        const visibleRows = rows.slice(pageStart, pageStart + PIVOT_PAGE_SIZE);
        const colSpanAll  = 3 + 2 + weekBuckets.length * 2;

        return (
        <>
            <div className="table-responsive avs-summary-table-wrap avs-summary-desktop-table" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {pivotLoading ? (
                    <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                        <RefreshCw size={14} style={{ animation: 'avs-spin 1s linear infinite' }} />
                        Loading summary...
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center py-4 text-muted">No matching records found.</div>
                ) : (
                    <table className="table table-bordered align-middle mb-0 avs-summary-table">
                        <thead className="sticky-top" style={{ zIndex: 10 }}>
                            <tr>
                                <th rowSpan={2} className="text-center align-middle avs-col-srno">Sr.no</th>
                                <th rowSpan={2} className="align-middle avs-col-company">Company Name</th>
                                <th rowSpan={2} className="align-middle avs-col-extra">{extraColLabel}</th>
                                <th colSpan={2} className="text-center avs-total-header">Total</th>
                                {weekBuckets.map((w, i) => (
                                    <th key={w.label} colSpan={2} className={`text-center avs-week-header ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{w.label}</th>
                                ))}
                            </tr>
                            <tr>
                                <th className="text-center avs-total-col-header avs-col-ai">AI Module</th>
                                <th className="text-center avs-total-col-header avs-col-manual">Manual</th>
                                {weekBuckets.map((w, i) => (
                                    <React.Fragment key={`${w.label}-sub`}>
                                        <th className={`text-center avs-week-divider avs-col-ai ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>AI Module</th>
                                        <th className={`text-center avs-col-manual ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>Manual</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row, idx) => (
                                <tr key={groupKeyOf(row)}>
                                    <td className="text-center avs-col-srno">{pageStart + idx + 1}</td>
                                    <td className="text-dark fw-medium avs-col-company avs-col-company-caps" title={(row.companyName || '').toUpperCase()}>{row.companyName}</td>
                                    <td className="avs-col-extra" title={String(row[extraColKey])}>{row[extraColKey]}</td>
                                    <td className="text-center avs-total-col avs-col-ai">{renderCount(row.total.ai)}</td>
                                    <td className="text-center avs-total-col avs-col-manual">{renderCount(row.total.manual)}</td>
                                    {weekBuckets.map((w, i) => (
                                        <React.Fragment key={`${groupKeyOf(row)}-${w.label}`}>
                                            <td className={`text-center avs-week-divider avs-col-ai ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{renderCount(row.weekly[w.label].ai)}</td>
                                            <td className={`text-center avs-col-manual ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{renderCount(row.weekly[w.label].manual)}</td>
                                        </React.Fragment>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            {totalPages > 1 && (
                                <tr>
                                    <td colSpan={colSpanAll} style={{ padding: '6px 10px', background: '#f8faf9', borderTop: '2px solid #dee2e6' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                                            <button
                                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                disabled={safePage === 1}
                                                style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid #d1fae5', background: safePage === 1 ? '#f3f4f6' : '#fff', color: safePage === 1 ? '#9ca3af' : BRAND_GREEN, cursor: safePage === 1 ? 'not-allowed' : 'pointer', fontSize: 12 }}
                                            >‹ Prev</button>
                                            <span style={{ fontSize: 12, color: '#6b7280' }}>
                                                Page {safePage} of {totalPages} &nbsp;·&nbsp; {totalRows} rows
                                            </span>
                                            <button
                                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                                disabled={safePage === totalPages}
                                                style={{ padding: '3px 8px', borderRadius: 5, border: '1px solid #d1fae5', background: safePage === totalPages ? '#f3f4f6' : '#fff', color: safePage === totalPages ? '#9ca3af' : BRAND_GREEN, cursor: safePage === totalPages ? 'not-allowed' : 'pointer', fontSize: 12 }}
                                            >Next ›</button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            <tr className="avs-grand-total-row">
                                <td colSpan={3} className="avs-col-company avs-grand-total-label">Grand Total</td>
                                <td className="text-center avs-total-col avs-col-ai">{grandTotal.total.ai}</td>
                                <td className="text-center avs-total-col avs-col-manual">{grandTotal.total.manual}</td>
                                {weekBuckets.map((w, i) => (
                                    <React.Fragment key={`gt-${w.label}`}>
                                        <td className={`text-center avs-week-divider avs-col-ai ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{grandTotal.weekly[w.label]?.ai || 0}</td>
                                        <td className={`text-center avs-col-manual ${i % 2 === 0 ? 'avs-week-even' : 'avs-week-odd'}`}>{grandTotal.weekly[w.label]?.manual || 0}</td>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>

            <div className="avs-summary-mobile-cards">
                {pivotLoading ? (
                    <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                        <RefreshCw size={14} style={{ animation: 'avs-spin 1s linear infinite' }} />
                        Loading summary...
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-center py-4 text-muted">No matching records found.</div>
                ) : (
                    <>
                        <div className="avs-scard avs-scard-total">
                            <div className="avs-scard-top">
                                <div className="avs-scard-title">
                                    <span className="avs-scard-total-label">Grand Total</span>
                                    <span className="avs-scard-total-sub">{rows.length} row{rows.length !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                            <div className="avs-scard-stats">
                                <div className="avs-scard-stat avs-scard-stat-ai">
                                    <span className="avs-scard-stat-value">{grandTotal.total.ai}</span>
                                    <span className="avs-scard-stat-label">AI Module</span>
                                </div>
                                <div className="avs-scard-stat avs-scard-stat-manual">
                                    <span className="avs-scard-stat-value">{grandTotal.total.manual}</span>
                                    <span className="avs-scard-stat-label">Manual</span>
                                </div>
                            </div>
                        </div>

                        {rows.map((row, idx) => {
                            const key = groupKeyOf(row);
                            const isExpanded = expandedGroups.has(key);
                            return (
                                <div key={key} className="avs-scard">
                                    <button
                                        type="button"
                                        className="avs-scard-top avs-scard-toggle"
                                        onClick={() => toggleExpand(key)}
                                        aria-expanded={isExpanded}
                                    >
                                        <div className="avs-scard-title">
                                            <span className="avs-scard-srno">{idx + 1}</span>
                                            <div className="avs-scard-title-text">
                                                <span className="avs-scard-company" title={row.companyName}>{row.companyName}</span>
                                                <span className="avs-scard-extra" title={String(row[extraColKey])}>{extraColLabel}: {row[extraColKey]}</span>
                                            </div>
                                        </div>
                                        <ChevronDown
                                            size={16}
                                            className={`avs-scard-chevron ${isExpanded ? 'avs-scard-chevron-open' : ''}`}
                                        />
                                    </button>

                                    <div className="avs-scard-stats">
                                        <div className="avs-scard-stat avs-scard-stat-ai">
                                            <span className="avs-scard-stat-value">{renderCount(row.total.ai)}</span>
                                            <span className="avs-scard-stat-label">AI Module</span>
                                        </div>
                                        <div className="avs-scard-stat avs-scard-stat-manual">
                                            <span className="avs-scard-stat-value">{renderCount(row.total.manual)}</span>
                                            <span className="avs-scard-stat-label">Manual</span>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="avs-scard-weeks">
                                            {weekBuckets.map((w) => (
                                                <div key={w.label} className="avs-scard-week-row">
                                                    <span className="avs-scard-week-label">{w.label}</span>
                                                    <span className="avs-scard-week-counts">
                                                        <span className="avs-scard-week-ai">{renderCount(row.weekly[w.label]?.ai || 0)} AI</span>
                                                        <span className="avs-scard-week-sep">·</span>
                                                        <span className="avs-scard-week-manual">{renderCount(row.weekly[w.label]?.manual || 0)} Manual</span>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>

            {renderTrendChart()}
        </>
        );
    };

    const renderWeeklyBillDetailBlocks = () => {
        if (pivotLoading) {
            return (
                <div className="text-center py-4 text-muted d-flex align-items-center justify-content-center gap-2">
                    <RefreshCw size={14} style={{ animation: 'avs-spin 1s linear infinite' }} />
                    Loading summary...
                </div>
            );
        }
        if (!weeklyBillDetailGroups.length) {
            return <div className="text-center py-4 text-muted">No matching records found.</div>;
        }

        return (
            <div className="avs-weekdetail-wrap">
                {weeklyBillDetailGroups.map((group) => (
                    <WeekDetailBlock
                        key={group.label}
                        group={group}
                        isCollapsed={collapsedWeekBlocks.has(group.label)}
                        onToggle={toggleWeekBlockCollapse}
                    />
                ))}
            </div>
        );
    };

    const renderDetailsTab = () => (
        <>
            <div className="d-flex justify-content-end px-2 pt-2">
                <div className="btn-group avs-view-toggle" role="group" style={{ height: '30px' }}>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: '12px', height: '30px',
                            backgroundColor: detailsViewMode === 'summary' ? BRAND_GREEN : '#fff',
                            color: detailsViewMode === 'summary' ? '#fff' : '#333',
                            border: '1px solid #dee2e6',
                        }}
                        onClick={() => handleDetailsViewModeChange('summary')}
                        title="Weekly summary — Company & Bill No"
                    >Weekly Summary</button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: '12px', height: '30px',
                            backgroundColor: detailsViewMode === 'detailed' ? BRAND_GREEN : '#fff',
                            color: detailsViewMode === 'detailed' ? '#fff' : '#333',
                            border: '1px solid #dee2e6', borderLeft: 'none',
                        }}
                        onClick={() => handleDetailsViewModeChange('detailed')}
                        title="Detailed view"
                    >Detailed View</button>
                </div>
            </div>

            {detailsViewMode === 'summary' ? (
                renderWeeklyBillDetailBlocks()
            ) : (
                <>
                    <div className="table-responsive avs-desktop-table" style={{ maxHeight: '60vh', overflowY: 'auto' }} ref={detailsTableRef}>
                        <table
                            className="table table-hover align-middle mb-0"
                            style={{ tableLayout: 'fixed', fontSize: '12.5px', width: '100%', minWidth: `${DETAILS_MIN_TABLE_PX}px` }}
                        >
                            <thead className="table-light sticky-top border-bottom" style={{ zIndex: 10 }}>
                                <tr>
                                    {DETAILS_COLUMNS.map((col, index) => (
                                        <th
                                            key={index}
                                            className="text-secondary fw-bold text-uppercase py-1 px-2 position-relative"
                                            style={thStyle(detailsColWidths[index], index === DETAILS_COLUMNS.length - 1, DETAILS_MIN_COL_PX[index])}
                                        >
                                            {col.label}
                                            <div
                                                onMouseDown={(e) => handleDetailsMouseDown(index, e)}
                                                style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 11 }}
                                                className="avs-column-resizer-handle"
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {details.loading ? (
                                    <tr><td colSpan={DETAILS_COLUMNS.length} className="text-center py-4 text-muted border-0">
                                        <RefreshCw size={16} style={{ animation: 'avs-spin 1s linear infinite', color: BRAND_GREEN }} />
                                    </td></tr>
                                ) : details.records.length === 0 ? (
                                    <tr><td colSpan={DETAILS_COLUMNS.length} className="text-center py-4 text-muted border-0">No matching records found.</td></tr>
                                ) : (
                                    details.records.map((row, idx) => {
                                        const badgeStyles = getSourceBadgeStyles(row.verificationSource);
                                        const source = (row.verificationSource || '').toLowerCase();
                                        return (
                                            <tr key={`${row.verificationId || idx}`} className={source === 'ai module' ? 'avs-row-ai' : source === 'manual' ? 'avs-row-manual' : ''}>
                                                <td className="py-1 px-2" style={tdStyle(detailsColWidths[0], false, DETAILS_MIN_COL_PX[0])} title={row.verificationSource || ''}>
                                                    {row.verificationSource ? (
                                                        <span className="badge fw-bold avs-px-15 avs-py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                            {row.verificationSource}
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                                <td className="py-1 px-2 text-dark fw-bold" title={row.companyName || ''} style={tdStyle(detailsColWidths[1], false, DETAILS_MIN_COL_PX[1])}>{row.companyName || '—'}</td>
                                                <td className="py-1 px-2" title={row.billNumber || ''} style={tdStyle(detailsColWidths[2], false, DETAILS_MIN_COL_PX[2])}>{row.billNumber || '—'}</td>
                                                <td className="py-1 px-2" title={row.verificationTypeName || ''} style={tdStyle(detailsColWidths[3], false, DETAILS_MIN_COL_PX[3])}>{row.verificationTypeName || '—'}</td>
                                                <td className="py-1 px-2" title={row.description || ''} style={tdStyle(detailsColWidths[4], false, DETAILS_MIN_COL_PX[4])}>{row.description || '—'}</td>
                                                <td className="text-secondary py-1 px-2" title={fmtDate(row.verificationDate)} style={tdStyle(detailsColWidths[5], false, DETAILS_MIN_COL_PX[5])}>{fmtDate(row.verificationDate)}</td>
                                                <td className="py-1 px-2" title={row.createdUserEmail || row.createdUserName || ''} style={tdStyle(detailsColWidths[6], false, DETAILS_MIN_COL_PX[6])}>{row.createdUserName || '—'}</td>
                                                <td className="text-secondary py-1 px-2" title={fmtDate(row.createdDate)} style={tdStyle(detailsColWidths[7], true, DETAILS_MIN_COL_PX[7])}>{fmtDate(row.createdDate)}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="avs-mobile-cards">
                        {details.records.length === 0 && !details.loading ? (
                            <div className="text-center py-4 text-muted">No matching records found.</div>
                        ) : (
                            details.records.map((row, idx) => (
                                <div key={`card-${row.verificationId || idx}`} className="avs-record-card">
                                    <div className="avs-record-card-grid">
                                        <div className="avs-record-field"><span className="avs-record-label">Source:</span><span className="avs-record-value">{row.verificationSource || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Comp Name:</span><span className="avs-record-value" style={{ fontWeight: 700, color: '#1a2420' }}>{row.companyName || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Bill No:</span><span className="avs-record-value">{row.billNumber || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Type:</span><span className="avs-record-value">{row.verificationTypeName || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Verified:</span><span className="avs-record-value">{fmtDate(row.verificationDate)}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Created By:</span><span className="avs-record-value">{row.createdUserName || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Created:</span><span className="avs-record-value">{fmtDate(row.createdDate)}</span></div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {renderPagination('Details', details)}
                </>
            )}
        </>
    );

    const renderUserBreakdownTab = () => (
        <>
            <div className="d-flex justify-content-end px-2 pt-2">
                <div className="btn-group avs-view-toggle" role="group" style={{ height: '30px' }}>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: '12px', height: '30px',
                            backgroundColor: ubViewMode === 'summary' ? BRAND_GREEN : '#fff',
                            color: ubViewMode === 'summary' ? '#fff' : '#333',
                            border: '1px solid #dee2e6',
                        }}
                        onClick={() => handleUbViewModeChange('summary')}
                        title="Weekly summary — Company & User"
                    >Weekly Summary</button>
                    <button
                        type="button"
                        className="btn btn-sm"
                        style={{
                            fontSize: '12px', height: '30px',
                            backgroundColor: ubViewMode === 'detailed' ? BRAND_GREEN : '#fff',
                            color: ubViewMode === 'detailed' ? '#fff' : '#333',
                            border: '1px solid #dee2e6', borderLeft: 'none',
                        }}
                        onClick={() => handleUbViewModeChange('detailed')}
                        title="Detailed view"
                    >Detailed View</button>
                </div>
            </div>

            {ubViewMode === 'summary' ? (
                renderPivotView({
                    rows: userPivotRows,
                    grandTotal: userGrandTotal,
                    extraColLabel: 'User Name',
                    extraColKey: 'userName',
                    expandedGroups: expandedUserGroups,
                    toggleExpand: toggleUserGroupExpand,
                    groupKeyOf: (row) => `${row.companyName}||${row.userName}`,
                    page: userPivotPage,
                    setPage: setUserPivotPage,
                })
            ) : (
                <>
                    <div className="table-responsive avs-desktop-table" style={{ maxHeight: '60vh', overflowY: 'auto' }} ref={ubTableRef}>
                        <table
                            className="table table-hover align-middle mb-0"
                            style={{ tableLayout: 'fixed', fontSize: '12.5px', width: '100%', minWidth: `${UB_MIN_TABLE_PX}px` }}
                        >
                            <thead className="table-light sticky-top border-bottom" style={{ zIndex: 10 }}>
                                <tr>
                                    {USER_BREAKDOWN_COLUMNS.map((col, index) => (
                                        <th
                                            key={index}
                                            className="text-secondary fw-bold text-uppercase py-1 px-2 position-relative"
                                            style={thStyle(ubColWidths[index], index === USER_BREAKDOWN_COLUMNS.length - 1, UB_MIN_COL_PX[index])}
                                        >
                                            {col.label}
                                            <div
                                                onMouseDown={(e) => handleUbMouseDown(index, e)}
                                                style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '6px', cursor: 'col-resize', zIndex: 11 }}
                                                className="avs-column-resizer-handle"
                                            />
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {userBreakdown.loading ? (
                                    <tr><td colSpan={USER_BREAKDOWN_COLUMNS.length} className="text-center py-4 text-muted border-0">
                                        <RefreshCw size={16} style={{ animation: 'avs-spin 1s linear infinite', color: BRAND_GREEN }} />
                                    </td></tr>
                                ) : userBreakdown.records.length === 0 ? (
                                    <tr><td colSpan={USER_BREAKDOWN_COLUMNS.length} className="text-center py-4 text-muted border-0">No matching records found.</td></tr>
                                ) : (
                                    userBreakdown.records.map((row, idx) => {
                                        const badgeStyles = getSourceBadgeStyles(row.verificationSource);
                                        const source = (row.verificationSource || '').toLowerCase();

                                        return (
                                            <tr key={`${row.userName || idx}-${row.companyName || idx}`} className={source === 'ai module' ? 'avs-row-ai' : source === 'manual' ? 'avs-row-manual' : ''
        }>
                                                <td className="py-1 px-2" style={tdStyle(ubColWidths[0], false, UB_MIN_COL_PX[0])} title={row.verificationSource || ''}>
                                                    {row.verificationSource ? (
                                                        <span className="badge fw-bold avs-px-15 avs-py-05" style={{ ...badgeStyles, borderRadius: '3px', fontSize: '10px' }}>
                                                            {row.verificationSource}
                                                        </span>
                                                    ) : '—'}
                                                </td>
                                                <td className="py-1 px-2 text-dark fw-bold" title={row.companyName || ''} style={tdStyle(ubColWidths[1], false, UB_MIN_COL_PX[1])}>{row.companyName || '—'}</td>
                                                <td className="py-1 px-2" title={row.userName || ''} style={tdStyle(ubColWidths[2], false, UB_MIN_COL_PX[2])}>{row.userName || '—'}</td>
                                                <td className="py-1 px-2" title={row.email || ''} style={tdStyle(ubColWidths[3], false, UB_MIN_COL_PX[3])}>{row.email || '—'}</td>
                                                <td className="py-1 px-2 fw-semibold" title={String(row.verificationCount ?? '')} style={tdStyle(ubColWidths[4], true, UB_MIN_COL_PX[4])}>{row.verificationCount ?? '—'}</td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div className="avs-mobile-cards">
                        {userBreakdown.records.length === 0 && !userBreakdown.loading ? (
                            <div className="text-center py-4 text-muted">No matching records found.</div>
                        ) : (
                            userBreakdown.records.map((row, idx) => (
                                <div key={`card-${row.userName || idx}-${row.companyName || idx}`} className="avs-record-card">
                                    <div className="avs-record-card-grid">
                                        <div className="avs-record-field"><span className="avs-record-label">Source:</span><span className="avs-record-value">{row.verificationSource || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Comp Name:</span><span className="avs-record-value" style={{ fontWeight: 700, color: '#1a2420' }}>{row.companyName || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">User:</span><span className="avs-record-value">{row.userName || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Email:</span><span className="avs-record-value">{row.email || '—'}</span></div>
                                        <div className="avs-record-field"><span className="avs-record-label">Count:</span><span className="avs-record-value">{row.verificationCount ?? '—'}</span></div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {renderPagination('UserBreakdown', userBreakdown)}
                </>
            )}
        </>
    );

    return (
        <div className="bg-light min-vh-100 avs-page-wrap">
            <Toaster position="top-right" />

            <div className="card shadow-sm border-0 rounded-3 bg-white avs-card">

                <div className="border-bottom px-2 py-2" style={{ backgroundColor: '#fcfcfc' }}>
                    <div className="d-flex gap-2 flex-grow-1 avs-toolbar-row" style={{ minWidth: 0 }}>
                        <div className="d-flex gap-2 flex-grow-1 avs-filters-row" style={{ minWidth: 0 }}>
                            <div className="avs-date-filter">
                                <label style={filterLabelStyle}>Created Date</label>
                                <DateRangePicker
                                    value={[fromDate || null, toDate || null]}
                                    onChange={(value) => {
                                        if (!value || value.length !== 2) {
                                            setFromDate(null);
                                            setToDate(null);
                                            return;
                                        }

                                        setFromDate(value[0]);
                                        setToDate(value[1]);
                                    }}
                                    ranges={predefinedBottomRanges}
                                    placeholder="Select Date Range"
                                    style={{ width: '100%' }}
                                    format="MM/dd/yyyy"
                                    size="sm"
                                    showOneCalendar={isMobile}
                                    placement="bottomStart"
                                    preventOverflow
                                    cleanable
                                    menuClassName="avs-daterange-menu"
                                />
                            </div>



                        </div>

                        <div className="d-flex gap-2 avs-toolbar-actions">
                            <button
                                className="btn text-white fw-medium d-flex align-items-center justify-content-center gap-1 px-3 avs-toolbar-btn"
                                style={{ backgroundColor: BRAND_GREEN, height: '32px', fontSize: '12px' }}
                                onClick={handleSearch} disabled={searchLoading} title="Search"
                            >
                                {searchLoading
                                    ? <RefreshCw size={13} style={{ animation: 'avs-spin 1s linear infinite', color: '#fff' }} />
                                    : <Search size={13} style={{ color: '#fff' }} />}
                                <span className="avs-btn-label">Search</span>
                            </button>

                            <button
                                className="btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 px-3 avs-toolbar-btn"
                                style={{ height: '32px', fontSize: '12px', backgroundColor: isExcelHovered ? '#f8f9fa' : '#fff', color: '#333' }}
                                onMouseEnter={() => setIsExcelHovered(true)}
                                onMouseLeave={() => setIsExcelHovered(false)}
                                onClick={exportToExcel} title={`Export ${activeTab} to Excel`}
                            >
                                <Download size={13} style={{ color: '#6c757d' }} />
                                <span className="avs-btn-label">Export</span>
                            </button>
                        </div>

                    </div>
                </div>

                <div className="avs-tabs-wrapper">
                    <button
                        className="avs-tab-arrow avs-tab-arrow-left"
                        onClick={() => scrollTabs("left")}
                    >
                        ❮
                    </button>

                    <div className="avs-tabs" ref={tabsRef}>
                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                className={`avs-tab-btn ${activeTab === tab.key ? "active" : ""}`}
                                onClick={() => startTransition(() => setActiveTab(tab.key))}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <button
                        className="avs-tab-arrow avs-tab-arrow-right"
                        onClick={() => scrollTabs("right")}
                    >
                        ❯
                    </button>
                </div>

                {activeTab === 'Summary'       && renderSummaryTab()}
                {activeTab === 'Details'       && renderDetailsTab()}
                {activeTab === 'UserBreakdown' && renderUserBreakdownTab()}
            </div>
        </div>
    );
};

export default AIVerificationSummaryReport;